import { hourFromAnchor, type ClockMsg } from '../../shared/clock';
import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import {
  CLOCK_HOURS_PER_SECOND,
  CLOCK_START_HOUR,
  TIMES_OF_DAY,
  effectiveTimeOfDay,
  forceMood,
  formatClock,
  moodTarget,
  moodsAtHour,
  setClockHour,
  setShardSky,
  setTimeOfDay,
  tickMoodSwitch,
  timeOfDayState,
  type TimeOfDay,
  type TimeOfDayService,
} from './mood';

/**
 * Time of day (owner: world-artist), order 140 — before the scene lighting reads the mood. Runs the
 * borough clock off simulated time (`ctx.state.time`, so pausing stops it and `?lockstep` runs are
 * repeatable): one game hour per 90 s (a 36-minute day), starting at 18:30 on every boot. With Settings → Time of day on
 * AUTO the clock cross-fades the moods (render/mood.ts); DAY / DUSK / NIGHT hold one. Missions may force
 * a mood for their duration through the `timeOfDay` service. A change of setting or job mood fades
 * over in `SWITCH_SECONDS` instead of cutting (rendered games only).
 *
 * Online the shard owns the sky (protocol v23, shared/clock.ts): the server's `clock` push anchors the hour
 * to its clock and names the weather; the hour then runs on the synced server time, so every player in a
 * shard sees the same light and rain. Joining at another hour fades over; the 60 s re-syncs carry on.
 * Leaving online hands back to the player's setting, the local clock carrying on from the shard's hour.
 */
export function createTimeOfDaySystem(): System {
  let ctx: GameContext;
  /** Clock hour at sim time 0 (moved by `setClockHour`). */
  let base = CLOCK_START_HOUR;
  /** Switches of setting / job mood fade over only where something is drawn (headless runs stay instant). */
  let fades = false;
  /** Online: the shard's last `clock` anchor (null offline). */
  let anchor: ClockMsg | null = null;

  const hourNow = (): number => (((base + ctx.state.time * CLOCK_HOURS_PER_SECOND) % 24) + 24) % 24;

  const service: TimeOfDayService = {
    get setting() {
      return effectiveTimeOfDay();
    },
    get shard() {
      return timeOfDayState.shard !== null;
    },
    options: TIMES_OF_DAY,
    set: (t) => setTimeOfDay(t, { fade: fades }),
    get hour() {
      return timeOfDayState.hour;
    },
    get forced() {
      return timeOfDayState.forced;
    },
    force: (id) => forceMood(id, { fade: fades }),
  };

  return {
    id: 'time-of-day',
    order: 140,
    init(c) {
      ctx = c;
      fades = !!c.render;
      c.services.provide('timeOfDay', service);
      setClockHour(hourNow());
      c.hooks.register('setTimeOfDay', (t) => setTimeOfDay(t as TimeOfDay));
      c.hooks.register('setClockHour', (h) => {
        if (!Number.isFinite(h)) return;
        base = h - ctx.state.time * CLOCK_HOURS_PER_SECOND;
        setClockHour(hourNow());
      });
      c.hooks.register('getTimeOfDay', () => {
        const s = timeOfDayState;
        const auto = effectiveTimeOfDay() === 'auto' && !s.forced;
        const b = auto ? moodsAtHour(s.hour) : null;
        return {
          setting: effectiveTimeOfDay(),
          shard: s.shard ? { weather: s.shard.weather } : null,
          hour: +s.hour.toFixed(3),
          clock: formatClock(s.hour),
          mood: moodTarget().id,
          weather: moodTarget().weather,
          forced: s.forced,
          blend: b && b.a !== b.b ? { a: b.a, b: b.b, t: +b.t.toFixed(3) } : null,
        };
      });
      // `__bh.setMood` (render system) goes through mood.ts `setMood`: it holds that mood as the setting.
    },
    start() {
      const listen = (): void => {
        const net = ctx.services.tryGet('net');
        net?.onMessage('clock', (d) => {
          const m = d as Partial<ClockMsg> | null;
          if (!m || !Number.isFinite(m.h) || !Number.isFinite(m.at) || (m.w !== 'clear' && m.w !== 'drizzle' && m.w !== 'rain')) return;
          anchor = { h: m.h!, at: m.at!, w: m.w };
          setShardSky({ hour: hourFromAnchor(anchor, net.serverNow()), weather: anchor.w }, { fade: fades });
        });
      };
      // The online client loads when the player goes online (net/onlineBoot.ts).
      const client = ctx.services.tryGet('onlineClient');
      if (client) client.whenLoaded(listen);
      else listen();
    },
    update(dt) {
      tickMoodSwitch(dt);
      const net = anchor ? ctx.services.tryGet('net') : undefined;
      if (anchor && net?.online) {
        setClockHour(hourFromAnchor(anchor, net.serverNow()));
        return;
      }
      if (anchor) {
        // Back to single-player: the local clock carries on from the shard's hour.
        anchor = null;
        base = timeOfDayState.hour - ctx.state.time * CLOCK_HOURS_PER_SECOND;
        setShardSky(null, { fade: fades });
      }
      setClockHour(hourNow());
    },
    dispose() {
      // Module state outlives a Game (tests boot several): don't leave a mission's mood or a shard's sky behind.
      forceMood(null);
      setShardSky(null);
    },
  };
}
